import { useEffect, useState } from "react";
import type { AdminRoleDto } from "./auth-api.js";
import { fetchGovernanceTenant } from "./governance-api.js";
import { getTenantExternalId } from "./tenant-settings.js";
import {
  resolveBetFlags,
  type ExperimentalBetFlags,
} from "@identity-armour/shared/experimental-bets/completeness-gate";

export type TenantFeatureKey =
  | "alerts"
  | "playbooks"
  | "threat_intel"
  | "onboarding_flow"
  | "aws_monitoring"
  | "pilot_access";

export type TenantFeatureFlags = Record<TenantFeatureKey, boolean>;

const DEFAULT_FLAGS: TenantFeatureFlags = {
  alerts: false,
  playbooks: false,
  threat_intel: false,
  onboarding_flow: false,
  aws_monitoring: false,
  pilot_access: false,
};

/** All experimental bets OFF — the safe default. */
export const DEFAULT_BET_FLAGS: ExperimentalBetFlags = {
  trust_sla: false,
  federated_psi: false,
  browser_native: false,
};

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/**
 * Resolve tenant-scoped feature flags from `tenants.settings`.
 *
 * Expected shape (booleans; defaults to false):
 * - settings.features.alerts
 * - settings.features.playbooks
 * - settings.features.threat_intel
 * - settings.features.aws_monitoring
 * - settings.features.pilot_access (platform-granted pilot program access)
 */
export function resolveTenantFeatureFlags(settings: Record<string, unknown>): TenantFeatureFlags {
  const featuresRaw = settings.features;
  const features = isRecord(featuresRaw) ? featuresRaw : {};

  return {
    alerts: features.alerts === true,
    playbooks: features.playbooks === true,
    threat_intel: features.threat_intel === true,
    onboarding_flow: features.onboarding_flow === true,
    aws_monitoring: features.aws_monitoring === true,
    pilot_access: features.pilot_access === true,
  };
}

/**
 * Resolve experimental-bet enablement flags from tenant `settings`.
 *
 * Delegates to the shared `resolveBetFlags` (in `@identity-armour/shared`) so the
 * dashboard and the API Gateway enforce the *same* completeness lift gate — a
 * bet stays OFF while its registry completenessScore is below the lift gate,
 * even if a tenant sets `settings.features.bets.<key> = true`.
 */
export function resolveTenantBetFlags(settings: Record<string, unknown>): ExperimentalBetFlags {
  return resolveBetFlags(settings);
}

type CacheEntry = { flags: TenantFeatureFlags; loadedAtMs: number };
const CACHE = new Map<string, CacheEntry>();
const CACHE_TTL_MS = 60_000;

type BetCacheEntry = { flags: ExperimentalBetFlags; loadedAtMs: number };
const BET_CACHE = new Map<string, BetCacheEntry>();

export function useTenantFeatureFlags(input: {
  role?: AdminRoleDto | null;
  tenantExternalId?: string | null;
}): { loading: boolean; flags: TenantFeatureFlags } {
  const tenantExternalId = (input.tenantExternalId ?? getTenantExternalId()).trim();

  const [state, setState] = useState<{ tenantExternalId: string; flags: TenantFeatureFlags }>(
    () => {
      const cached = tenantExternalId ? CACHE.get(tenantExternalId) : undefined;
      return { tenantExternalId, flags: cached?.flags ?? DEFAULT_FLAGS };
    }
  );
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!tenantExternalId) return;
    const entry = CACHE.get(tenantExternalId);
    if (entry) {
      const ageMs = Date.now() - entry.loadedAtMs;
      if (ageMs <= CACHE_TTL_MS) {
        return;
      }
      CACHE.delete(tenantExternalId);
    }

    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled) setLoading(true);
    });
    void fetchGovernanceTenant(tenantExternalId)
      .then((t) => {
        const resolved = resolveTenantFeatureFlags(t.settings ?? {});
        CACHE.set(tenantExternalId, { flags: resolved, loadedAtMs: Date.now() });
        if (!cancelled) setState({ tenantExternalId, flags: resolved });
      })
      .catch(() => {
        if (!cancelled) setState({ tenantExternalId, flags: DEFAULT_FLAGS });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tenantExternalId, input.role]);

  const cached = tenantExternalId ? CACHE.get(tenantExternalId) : undefined;
  const flags =
    state.tenantExternalId === tenantExternalId
      ? state.flags
      : cached?.flags ?? DEFAULT_FLAGS;

  return { loading, flags };
}

/**
 * Resolve experimental-bet flags for the current tenant. Shares the same fetch
 * + cache lifecycle as `useTenantFeatureFlags` but exposes the bet sub-flags so
 * nav links and route guards can gate the three innovation bets.
 */
export function useTenantBetFlags(input: {
  role?: AdminRoleDto | null;
  tenantExternalId?: string | null;
}): { loading: boolean; flags: ExperimentalBetFlags } {
  const tenantExternalId = (input.tenantExternalId ?? getTenantExternalId()).trim();

  const [state, setState] = useState<{
    tenantExternalId: string;
    flags: ExperimentalBetFlags;
  }>(() => {
    const cached = tenantExternalId ? BET_CACHE.get(tenantExternalId) : undefined;
    return { tenantExternalId, flags: cached?.flags ?? DEFAULT_BET_FLAGS };
  });
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (!tenantExternalId) return;
    const entry = BET_CACHE.get(tenantExternalId);
    if (entry) {
      const ageMs = Date.now() - entry.loadedAtMs;
      if (ageMs <= CACHE_TTL_MS) {
        return;
      }
      BET_CACHE.delete(tenantExternalId);
    }

    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled) setLoading(true);
    });
    void fetchGovernanceTenant(tenantExternalId)
      .then((t) => {
        const resolved = resolveTenantBetFlags(t.settings ?? {});
        BET_CACHE.set(tenantExternalId, { flags: resolved, loadedAtMs: Date.now() });
        if (!cancelled) setState({ tenantExternalId, flags: resolved });
      })
      .catch(() => {
        if (!cancelled) setState({ tenantExternalId, flags: DEFAULT_BET_FLAGS });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tenantExternalId, input.role]);

  const cached = tenantExternalId ? BET_CACHE.get(tenantExternalId) : undefined;
  const flags =
    state.tenantExternalId === tenantExternalId
      ? state.flags
      : cached?.flags ?? DEFAULT_BET_FLAGS;

  return { loading, flags };
}

